'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { XCircleIcon } from '@heroicons/react/24/outline'
import * as Sentry from '@sentry/nextjs'

import Spinner from '@/app/[locale]/(main-layout)/components/ui/Spinner'

export default function Iframe({
  url,
  labels
}: { url: string } & {
  labels: {
    TITLE: string
    SUBTITLE: string
    BUTTON: string
  }
}) {
  const [isLoading, setIsLoading] = useState(true)
  const [isError, setIsError] = useState(false)
  const [iframeHeight, setIframeHeight] = useState<number | null>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const isFirstLoad = useRef(true)
  const isFirstNavigation = useRef(true)

  const handleLoad = () => {
    setIsLoading(false)
    if (isFirstLoad.current) {
      isFirstLoad.current = false
      return
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      try {
        const iframeOrigin = new URL(url).origin
        if (event.origin !== iframeOrigin) {
          return
        }
      } catch {
        return
      }

      if (
        event.data &&
        event.data.type === 'iframe-resize' &&
        typeof event.data.height === 'number'
      ) {
        setIframeHeight(event.data.height)
      }

      if (event.data && event.data.type === 'iframe-navigation') {
        if (isFirstNavigation.current) {
          isFirstNavigation.current = false
          return
        }
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
      }
    },
    [url]
  )

  useEffect(() => {
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [handleMessage])

  useEffect(() => {
    const checkUrl = async () => {
      try {
        const response = await fetch(url, { method: 'HEAD' })
        if (!response.ok) {
          Sentry.captureException(
            new Error(`Error preloading iframe: ${response.statusText}`)
          )
          setIsError(true)
        }
      } catch (err) {
        Sentry.captureException(`Error preloading iframe: ${err}`)
        setIsError(true)
      } finally {
        setIsLoading(false)
      }
    }

    checkUrl()
  }, [url])

  return (
    <>
      {isLoading && (
        <div className="absolute left-0 top-0 z-10 grid h-full min-h-screen w-full place-content-center bg-white">
          <Spinner className="text-slate-500" variant />
        </div>
      )}
      {isError ? (
        <section className="flex min-h-[80vh]  w-full flex-grow items-center justify-center bg-brand-gray p-8">
          <div className="flex flex-col items-center gap-4">
            <XCircleIcon width={70} />
            <div className="text-center">
              <h1 className="text-base font-medium text-brand-blue">
                {labels.TITLE}
              </h1>
              <h2 className="text-base font-light text-brand-blue">
                {labels.SUBTITLE}
              </h2>
            </div>
          </div>
        </section>
      ) : (
        <iframe
          ref={iframeRef}
          src={`${url}?no_load_website_body_scripts=1&no_load_website_head_scripts=1`}
          title="Contenido cargado"
          width="100%"
          style={{
            border: 'none',
            height: iframeHeight ? `${iframeHeight}px` : '100vh',
            overflow: 'hidden'
          }}
          scrolling="no"
          allowFullScreen
          onLoad={handleLoad}
          onError={() => {
            console.error('Error loading iframe content')
            setIsError(true)
            setIsLoading(false)
          }}
        />
      )}
    </>
  )
}
